// Response schemas for /tasks and the task page (plan §4, spec §5.1). Shapes follow
// apps/api/src/modules/tasks/list-service.ts, detail-service.ts and history/service.ts; unknown keys are dropped. Leads text is already
// redacted by the API for team roles (constitution §6.4); the client only parses.
import { TASK_ACTIONS, TASK_STATUSES } from '@cis/shared';
import { z } from 'zod';

// Text caps are the server's column caps times REDACTION_HEADROOM: for team roles the API replaces
// each Leads id ("R1", 2 chars) with "[Leads only]" (12 chars) after the cap was applied, so a
// legal title near 500 chars can arrive longer, and one long field must not fail the whole page
// (review D-R24). The caps stay as a guard against an absurd answer.
const REDACTION_HEADROOM = 6;
const MAX_ID = 64;
const MAX_LABEL = 500 * REDACTION_HEADROOM;
const MAX_DATE = 10;
const MAX_PAGE_ITEMS = 100;
const MAX_LIST = 500;
const MAX_PERCENT = 100;

export const taskStatusSchema = z.enum(TASK_STATUSES);

// A calendar date as the API sends it: "2026-11-04".
const calendarDate = z.string().max(MAX_DATE);

export const personSchema = z.object({
  id: z.number().int().positive(),
  displayName: z.string().max(MAX_LABEL),
});

export const taskListItemSchema = z.object({
  taskId: z.string().min(1).max(MAX_ID),
  title: z.string().max(MAX_LABEL),
  phase: z.string().max(MAX_ID),
  area: z.string().max(MAX_LABEL),
  lane: z.string().max(MAX_ID),
  tier: z.string().max(MAX_ID),
  families: z.array(z.unknown()).max(MAX_LIST),
  safeguards: z.array(z.unknown()).max(MAX_LIST),
  plannedStart: calendarDate.nullable(),
  plannedEnd: calendarDate.nullable(),
  replannedStart: calendarDate.nullable(),
  replannedEnd: calendarDate.nullable(),
  due: calendarDate.nullable(),
  status: taskStatusSchema,
  percent: z.number().int().min(0).max(MAX_PERCENT),
  overdue: z.boolean(),
  retired: z.boolean(),
  successorTaskId: z.string().max(MAX_ID).nullable(),
  assignees: z.array(personSchema).max(MAX_LIST),
  version: z.number().int().nonnegative(),
});

export const taskPageSchema = z.object({
  items: z.array(taskListItemSchema).max(MAX_PAGE_ITEMS),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
});

export type Person = z.infer<typeof personSchema>;
export type TaskListItem = z.infer<typeof taskListItemSchema>;
export type TaskPage = z.infer<typeof taskPageSchema>;

// The task page: GET /tasks/:taskId, also the answer of every task write.

const MAX_TEXT = 20_000 * REDACTION_HEADROOM;
const MAX_STAMP = 64;
const MAX_DETAIL_LIST = 1000;
const MAX_HISTORY_PAGE = 100;

const text = z.string().max(MAX_TEXT);
const nullableText = text.nullable();
const stamp = z.string().max(MAX_STAMP);
const people = z.array(personSchema).max(MAX_DETAIL_LIST);

export const taskActionSchema = z.enum(TASK_ACTIONS);

export const taskStepSchema = z.object({
  no: z.number().int().positive(),
  do: text,
  how: nullableText,
  check: nullableText,
  who: nullableText,
  required: z.boolean(),
  assignees: people,
  tick: z.object({ by: personSchema, at: stamp, textChanged: z.boolean() }).nullable(),
  canTick: z.boolean(),
});

export const taskStateSchema = z.object({
  status: taskStatusSchema,
  prevStatus: taskStatusSchema.nullable(),
  percent: z.number().int().min(0).max(MAX_PERCENT),
  percentOverrideNote: nullableText,
  actualFinish: nullableText,
  updateNotes: nullableText,
  blockedReason: nullableText,
  deferredReason: nullableText,
  replannedStart: nullableText,
  replannedEnd: nullableText,
  version: z.number().int().nonnegative(),
});

export const taskDetailSchema = z.object({
  taskId: z.string().min(1).max(MAX_ID),
  title: z.string().max(MAX_LABEL),
  phase: z.object({
    code: z.string().max(MAX_ID),
    title: z.string().max(MAX_LABEL).nullable().optional(),
    start: z.string().max(MAX_STAMP).nullable().optional(),
    end: z.string().max(MAX_STAMP).nullable().optional(),
  }),
  area: z.string().max(MAX_LABEL),
  lane: z.string().max(MAX_ID),
  tier: z.string().max(MAX_ID),
  families: z.array(z.unknown()).max(MAX_DETAIL_LIST),
  safeguards: z.array(z.unknown()).max(MAX_DETAIL_LIST),
  plannedStart: z.string().max(MAX_STAMP).nullable(),
  plannedEnd: z.string().max(MAX_STAMP).nullable(),
  due: z.string().max(MAX_STAMP).nullable(),
  overdue: z.boolean(),
  retired: z
    .object({ at: stamp.optional(), successorTaskId: z.string().max(MAX_ID).nullable() })
    .nullable(),
  content: z.unknown(),
  state: taskStateSchema,
  assignees: people,
  steps: z.array(taskStepSchema).max(MAX_DETAIL_LIST),
  dependencies: z
    .array(
      z.object({
        taskId: z.string().max(MAX_ID),
        title: z.string().max(MAX_LABEL),
        retired: z.boolean(),
        successorTaskId: z.string().max(MAX_ID).nullable(),
      }),
    )
    .max(MAX_DETAIL_LIST),
  permissions: z.object({
    // An action this build does not know is dropped (fails closed: no button for it).
    actions: z
      .array(z.string())
      .max(MAX_DETAIL_LIST)
      .transform((list) =>
        list.flatMap((a) => {
          const parsed = taskActionSchema.safeParse(a);
          return parsed.success ? [parsed.data] : [];
        }),
      ),
    assign: z.boolean(),
    replan: z.boolean(),
    // PATCH /tasks/:taskId (M5-R3). An answer without them offers no edit (fails closed).
    edit: z.boolean().default(false),
    overridePercent: z.boolean().default(false),
  }),
  asOf: z.string().max(MAX_STAMP).nullable(),
});

export const historyEntrySchema = z.object({
  id: z.number().int(),
  at: stamp,
  user: personSchema.nullable(),
  entityType: z.string().max(MAX_ID),
  entityKey: z.string().max(MAX_ID),
  action: z.string().max(MAX_ID),
  before: z.unknown(),
  after: z.unknown(),
  purged: z.boolean(),
});

export const historyPageSchema = z.object({
  items: z.array(historyEntrySchema).max(MAX_HISTORY_PAGE),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
});

// Runbook prose kept in the task's pack content (goal, before you start...). Anything missing or
// of another shape is simply not shown.
const textList = z.array(z.string().max(MAX_TEXT)).max(MAX_DETAIL_LIST).catch([]);
export const taskContentSchema = z
  .object({
    description: z.string().max(MAX_TEXT).catch(''),
    runbook: z
      .object({
        goal: z.string().max(MAX_TEXT).catch(''),
        beforeYouStart: textList,
        doneWhen: textList,
        pitfalls: textList,
        escalateIf: textList,
        rollback: z.string().max(MAX_TEXT).catch(''),
      })
      .nullable()
      .catch(null),
  })
  .catch({ description: '', runbook: null });

export type TaskAction = z.infer<typeof taskActionSchema>;
export type TaskStep = z.infer<typeof taskStepSchema>;
export type TaskState = z.infer<typeof taskStateSchema>;
export type TaskDetail = z.infer<typeof taskDetailSchema>;
export type HistoryEntry = z.infer<typeof historyEntrySchema>;
export type HistoryPage = z.infer<typeof historyPageSchema>;
export type TaskContent = z.infer<typeof taskContentSchema>;
